import { useEffect, useRef, useState } from 'react';
import type { Table as ReactTable } from '@tanstack/react-table';

export type VisibleColumnRange = { startIndex: number; endIndex: number } | null;

type UseVisibleColumnRangeReturn = {
  isScrolling: boolean;
  visibleColumnRange: VisibleColumnRange;
};

/**
 * Tracks scroll state and the visible column index range for horizontal virtualization.
 *
 * - `isScrolling` is set true on scroll and reset 900ms after the last scroll event.
 * - `visibleColumnRange` reflects which leaf columns intersect the viewport (with a
 *   small overscan), recomputed in rAF so it stays in sync with the frame.
 *   Returns `null` when no horizontal scroll is needed.
 */
export const useVisibleColumnRange = <T>(
  table: ReactTable<T>,
  containerRef: React.RefObject<HTMLDivElement>
): UseVisibleColumnRangeReturn => {
  const [isScrolling, setIsScrolling] = useState(false);
  const [visibleColumnRange, setVisibleColumnRange] = useState<VisibleColumnRange>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRafRef = useRef<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setIsScrolling(true);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
        setVisibleColumnRange(null);
      }, 900);

      if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const containerEl = containerRef.current;
        if (!containerEl) return;
        const left = containerEl.scrollLeft;
        const clientWidth = containerEl.clientWidth;
        const scrollWidth = containerEl.scrollWidth;
        if (scrollWidth <= clientWidth) {
          setVisibleColumnRange(null);
          return;
        }
        const columns = table.getVisibleLeafColumns();
        let colLeft = 0;
        const overscan = 150;
        const viewStart = left - overscan;
        const viewEnd = left + clientWidth + overscan;
        let startIndex = -1;
        let endIndex = -1;
        for (let i = 0; i < columns.length; i++) {
          const col = columns[i];
          const width = col.getSize();
          const colEnd = colLeft + width;
          if (colEnd > viewStart && colLeft < viewEnd) {
            if (startIndex === -1) startIndex = i;
            endIndex = i;
          }
          colLeft = colEnd;
        }
        if (startIndex >= 0 && endIndex >= 0) {
          setVisibleColumnRange({ startIndex, endIndex });
        }
      });
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
    };
  }, [table, containerRef]);

  return { isScrolling, visibleColumnRange };
};
